import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { API } from "../../App";

import {
  OverviewTab,
  PowerPointTab,
  BusinessPlanTab,
  BusinessCaseTab,
  CadrageTab,
  SwotTab,
  RoadmapTab,
  ActionPlanTab,
  BudgetTab,
  RisksTab,
  GovernanceTab,
  FinancingTab,
  CopilTab,
  ComexTab,
} from "../../components/projects/ProjectTabs";
import { PhasesAndTasksTab } from "../../components/projects/PhasesAndTasksTab";

const TABS = [
  { value: "overview", label: "Vue d'ensemble" },
  { value: "phasesTasks", label: "Phases & Tâches" },
  { value: "powerpoint", label: "PowerPoint" },
  { value: "cadrage", label: "Note de cadrage" },
  { value: "businessPlan", label: "Business plan" },
  { value: "businessCase", label: "Business case" },
  { value: "swot", label: "SWOT" },
  { value: "roadmap", label: "Roadmap" },
  { value: "actionPlan", label: "Plan d'action" },
  { value: "budget", label: "Budget" },
  { value: "risks", label: "Risques" },
  { value: "governance", label: "Gouvernance" },
  { value: "financing", label: "Financement" },
  { value: "copil", label: "COPIL" },
  { value: "comex", label: "COMEX" },
];

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [deliverables, setDeliverables] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, deliverablesRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}`, { credentials: "include" }),
        fetch(`${API}/projects/${projectId}/deliverables`, { credentials: "include" }),
      ]);

      if (projectRes.status === 404) {
        setNotFound(true);
        return;
      }

      if (projectRes.ok && deliverablesRes.ok) {
        setProject(await projectRes.json());
        setDeliverables(await deliverablesRes.json());
      }
    } catch (error) {
      console.error("Error fetching project:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <p className="text-lg font-semibold text-slate-900 dark:text-white">
            Projet introuvable.
          </p>
          <Button
            onClick={() => navigate("/dashboard/projects")}
            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white"
          >
            Retour aux projets
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
          {project.brief?.name || "Projet sans nom"}
        </h1>
        <Badge className="bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300">
          {project.status}
        </Badge>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="inline-flex h-auto w-max gap-1 bg-transparent p-0">
            {TABS.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-lg px-3.5 py-2 text-sm data-[state=active]:bg-blue-100 dark:data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-700 dark:data-[state=active]:text-blue-300"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="mt-6">
          <TabsContent value="overview"><OverviewTab project={project} /></TabsContent>
          <TabsContent value="phasesTasks"><PhasesAndTasksTab project={project} /></TabsContent>
          <TabsContent value="powerpoint"><PowerPointTab project={project} deliverables={deliverables} /></TabsContent>
          <TabsContent value="cadrage"><CadrageTab project={project} /></TabsContent>
          <TabsContent value="businessPlan"><BusinessPlanTab deliverables={deliverables} /></TabsContent>
          <TabsContent value="businessCase"><BusinessCaseTab deliverables={deliverables} /></TabsContent>
          <TabsContent value="swot"><SwotTab project={project} /></TabsContent>
          <TabsContent value="roadmap"><RoadmapTab project={project} /></TabsContent>
          <TabsContent value="actionPlan"><ActionPlanTab project={project} /></TabsContent>
          <TabsContent value="budget"><BudgetTab project={project} /></TabsContent>
          <TabsContent value="risks"><RisksTab project={project} /></TabsContent>
          <TabsContent value="governance"><GovernanceTab project={project} /></TabsContent>
          <TabsContent value="financing"><FinancingTab project={project} deliverables={deliverables} /></TabsContent>
          <TabsContent value="copil"><CopilTab project={project} /></TabsContent>
          <TabsContent value="comex"><ComexTab project={project} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default ProjectDetailPage;